/**
 * @fileoverview Job Award Management Hook
 * 
 * Manages the complete job award workflow from selecting a winning bid to
 * initiating payment setup. This hook orchestrates multiple database operations
 * and notifications to ensure a successful award process.
 * 
 * Award Process Flow:
 * 1. Client selects specific line items from winning bid
 * 2. Client confirms/edits distribution quantities (if multiple locations)
 * 3. System creates award record with total amount (if not exists)
 * 4. Award selections are recorded (linking specs to bid line items)
 * 5. Distribution quantities are finalized
 * 6. Job status updated to 'awarded'
 * 7. RFQ closed
 * 8. Winning bid marked as 'winner', others as 'loser'
 * 9. Payment setup initialized for both client and vendor
 * 10. Vendor notified of award
 * 11. User redirected to payment setup page
 * 
 * @module useJobAward
 */

import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useToast } from '@/hooks/use-toast';
import { useCreatePurchaseOrder } from '@/hooks/usePurchaseOrders';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { DistributionQuantities } from '@/components/jobs/AwardSelectionModal';
import { analytics, ANALYTICS_EVENTS } from '@/lib/analytics';

/**
 * Represents a selected specification from a bid to be included in the award.
 */
interface AwardSelection {
  specification_id: string;
  quantity_option: number;
  bid_line_item_id: string;
  unit_price_cents: number;
  total_price_cents: number;
}

/**
 * Feedback data capturing why the client selected the winning vendor
 */
export interface AwardFeedback {
  selectionReasons: string[];
  additionalComments?: string;
}

/**
 * Hook for awarding jobs to winning vendors
 * 
 * Provides comprehensive award management including:
 * - Award record creation with selected line items
 * - Distribution quantity finalization
 * - Bid status updates (winner/loser)
 * - Job and RFQ status progression
 * - Payment setup initialization with organization validation
 * - Vendor notification
 * - Automatic navigation to payment setup
 * 
 * @returns Object with awardJob function and loading state
 */
export const useJobAward = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { createPurchaseOrder } = useCreatePurchaseOrder();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  /**
   * Awards a job to a winning vendor
   * 
   * @param jobId - The UUID of the job to award
   * @param winningBidId - The UUID of the selected winning bid
   * @param selections - Array of award selections (line items from the bid)
   * @param awardNotes - Optional notes explaining the award decision
   * @param distributionQuantities - Optional finalized distribution quantities per location
   * @param recalculatedTax - Optional recalculated tax data from distribution changes
   * @param feedbackData - Optional feedback about why the vendor was selected
   * @throws Error if validation fails or any database operation fails
   * @returns Award data object
   */
  /**
   * Recalculated tax data from distribution changes at award time
   */
  interface RecalculatedTaxData {
    totalTaxCents: number;
    taxBySpec: Record<string, number>;
    /** Per-location tax calculation details for detailed PO breakdown */
    calculations?: Array<{
      specificationId: string;
      locationId: string;
      quantity: number;
      taxableAmountCents: number;
      taxCents: number;
      taxRate: number;
    }>;
  }

  const awardJob = async (
    jobId: string, 
    winningBidId: string, 
    selections: AwardSelection[],
    awardNotes?: string,
    distributionQuantities?: DistributionQuantities,
    recalculatedTax?: RecalculatedTaxData,
    feedbackData?: AwardFeedback
  ) => {
    try {
      setLoading(true);

      // Check for existing award (idempotency check)
      const { data: existingAward, error: checkError } = await supabase
        .from('awards')
        .select('id')
        .eq('job_id', jobId)
        .maybeSingle();

      if (checkError) {
        console.error('[useJobAward] Error checking for existing award:', checkError);
        throw checkError;
      }

      let awardData = existingAward;

      // Only create award if one doesn't exist
      if (!existingAward) {
        console.log('[useJobAward] No existing award found, creating new award');
        
        // Calculate total award amount from selections
        const totalAwardAmountCents = selections.reduce((sum, sel) => sum + sel.total_price_cents, 0);

        // Create award record - include recalculated tax if distribution was modified
        const { data: newAward, error: awardError } = await supabase
          .from('awards')
          .insert({
            job_id: jobId,
            winning_bid_id: winningBidId,
            awarded_by_user_id: (await supabase.auth.getUser()).data.user?.id || '',
            award_notes: awardNotes,
            total_award_amount_cents: totalAwardAmountCents,
            // Store recalculated tax from distribution changes at award time
            total_recalculated_tax_cents: recalculatedTax?.totalTaxCents ?? null,
            // Store the full calculations array for per-destination tax breakdown on PO
            tax_recalculation_breakdown: recalculatedTax?.calculations ?? null,
            tax_recalculated_at: recalculatedTax ? new Date().toISOString() : null,
          })
          .select()
          .single();

        if (awardError) throw awardError;
        awardData = newAward;

        // Insert award selections (only for new awards)
        const awardSelections = selections.map(sel => ({
          award_id: newAward.id,
          specification_id: sel.specification_id,
          quantity_option: sel.quantity_option,
          bid_line_item_id: sel.bid_line_item_id,
          unit_price_cents: sel.unit_price_cents,
          total_price_cents: sel.total_price_cents
        }));

        const { error: selectionsError } = await supabase
          .from('award_selections')
          .insert(awardSelections);

        if (selectionsError) throw selectionsError;
      } else {
        console.log('[useJobAward] Existing award found, recovering from partial failure:', existingAward.id);
      }

      // Finalize distribution quantities if provided
      if (distributionQuantities && Object.keys(distributionQuantities).length > 0) {
        console.log('[useJobAward] Finalizing distribution quantities');
        
        const userId = (await supabase.auth.getUser()).data.user?.id;
        
        for (const specId of Object.keys(distributionQuantities)) {
          const locationQuantities = distributionQuantities[specId];
          
          for (const locationId of Object.keys(locationQuantities)) {
            const quantity = locationQuantities[locationId];
            
            // Upsert the distribution quantity with is_finalized = true
            const { error: upsertError } = await supabase
              .from('job_specification_location_quantities')
              .upsert({
                job_id: jobId,
                specification_id: specId,
                delivery_location_id: locationId,
                quantity: quantity,
                is_finalized: true,
                created_by_user_id: userId,
                last_updated_by_user_id: userId
              }, {
                onConflict: 'job_id,specification_id,delivery_location_id'
              });

            if (upsertError) {
              console.error('[useJobAward] Error upserting distribution quantity:', upsertError);
              // Continue with other updates, don't fail the entire award
            }
          }
        }
        
        console.log('[useJobAward] Distribution quantities finalized');
        
        // Invalidate tax preview cache so milestones page gets fresh tax data
        await queryClient.invalidateQueries({ queryKey: ['tax-preview', jobId] });
        console.log('[useJobAward] Tax preview cache invalidated');
      }

      // Always update statuses (idempotent - safe to run multiple times)
      console.log('[useJobAward] Updating job status to awarded');
      await supabase
        .from('jobs')
        .update({ status: 'awarded' })
        .eq('id', jobId);

      console.log('[useJobAward] Updating RFQ status to closed');
      await supabase
        .from('rfqs')
        .update({ status: 'closed' })
        .eq('job_id', jobId);

      console.log('[useJobAward] Updating winning bid status');
      await supabase
        .from('bids')
        .update({ status: 'winner' })
        .eq('id', winningBidId);

      // Get the RFQ ID for the winning bid to update losing bids
      const { data: winningBid } = await supabase
        .from('bids')
        .select('rfq_id')
        .eq('id', winningBidId)
        .single();

      if (winningBid) {
        console.log('[useJobAward] Updating losing bids status');
        await supabase
          .from('bids')
          .update({ status: 'loser' })
          .eq('rfq_id', winningBid.rfq_id)
          .neq('id', winningBidId);
      }

      // Determine target path using current URL if available to preserve public IDs
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      const pathMatch = currentPath.match(/^\/jobs\/([^\/?#]+)/);
      const urlJobId = pathMatch?.[1];
      let targetPath = `/jobs/${urlJobId || jobId}/payment-setup`; // Default fallback

      // Always initialize payment setup - server derives org IDs authoritatively
      console.log('[useJobAward] ========== PAYMENT SETUP INITIALIZATION START ==========');
      console.log('[useJobAward] Calling init-payment-setup edge function for job:', jobId);
      console.log('[useJobAward] URL jobId:', urlJobId);
      
      const { data: initData, error: initError } = await invokeEdgeFunction('init-payment-setup', {
        body: { jobId },
        callerOperation: 'useJobAward.initPaymentSetup',
      });

      console.log('[useJobAward] init-payment-setup RAW RESPONSE:', {
        data: initData,
        error: initError,
        dataType: typeof initData,
        errorType: typeof initError,
        dataKeys: initData ? Object.keys(initData) : null
      });

      if (initError) {
        console.error('[useJobAward] ❌ ERROR initializing payment setup:', initError);
        console.log('[useJobAward] Defaulting to payment-setup page due to error');
        
        toast({
          title: "Warning",
          description: "Payment setup initialization failed. You may need to set it up manually.",
          variant: "destructive"
        });
      } else {
        // Type the init data
        interface PaymentSetupResult {
          both_complete?: boolean;
          client_setup_complete?: boolean;
          vendor_setup_complete?: boolean;
          success?: boolean;
          payment_setup?: unknown;
        }
        const paymentSetup = initData as PaymentSetupResult;
        
        console.log('[useJobAward] ✅ Payment setup initialized successfully');
        console.log('[useJobAward] Parsed payment setup values:', {
          both_complete: paymentSetup?.both_complete,
          client_setup_complete: paymentSetup?.client_setup_complete,
          vendor_setup_complete: paymentSetup?.vendor_setup_complete,
          success: paymentSetup?.success,
          payment_setup: paymentSetup?.payment_setup
        });
        
        // Check if both parties have completed payment setup
        const bothComplete = paymentSetup?.both_complete === true;
        console.log('[useJobAward] both_complete check result:', {
          value: paymentSetup?.both_complete,
          typeOf: typeof paymentSetup?.both_complete,
          strictEqual: bothComplete,
          looseEqual: paymentSetup?.both_complete == true
        });
        
        if (bothComplete) {
          console.log('[useJobAward] ✅ BOTH PARTIES COMPLETE - Redirecting to milestones');
          targetPath = `/jobs/${urlJobId || jobId}/milestones`;
          
          toast({
            title: "Job Awarded & Payment Setup Complete",
            description: "Payment setup complete. You can now manage milestones and approve payment."
          });
        } else {
          console.log('[useJobAward] ⚠️ PAYMENT SETUP INCOMPLETE - Redirecting to payment-setup page');
          console.log('[useJobAward] Setup status details:', {
            clientSetup: paymentSetup?.client_setup_complete,
            vendorSetup: paymentSetup?.vendor_setup_complete,
            bothComplete: paymentSetup?.both_complete
          });
          
          toast({
            title: "Job Awarded Successfully",
            description: "Next: Complete payment setup with vendor"
          });
        }
      }
      
      console.log('[useJobAward] Final targetPath determined:', targetPath);
      console.log('[useJobAward] ========== PAYMENT SETUP INITIALIZATION END ==========');

      // Get bid count for analytics
      const { count: bidCount } = await supabase
        .from('bids')
        .select('*', { count: 'exact', head: true })
        .eq('rfq_id', winningBid?.rfq_id);

      // Calculate award amount from selections for analytics
      const totalAwardAmount = selections.reduce((sum, sel) => sum + sel.total_price_cents, 0);

      // Track job awarded event
      analytics.track({
        name: ANALYTICS_EVENTS.JOB_AWARDED,
        category: 'transaction',
        properties: {
          job_id: jobId,
          winning_bid_id: winningBidId,
          bid_count: bidCount || 0,
          award_amount_cents: totalAwardAmount,
        },
      });

      // Track losing bids
      if (winningBid) {
        const { data: losingBids } = await supabase
          .from('bids')
          .select('id, vendor_user_id')
          .eq('rfq_id', winningBid.rfq_id)
          .neq('id', winningBidId);
        
        losingBids?.forEach(bid => {
          analytics.track({
            name: ANALYTICS_EVENTS.QUOTE_LOST,
            category: 'transaction',
            properties: {
              bid_id: bid.id,
              job_id: jobId,
            },
          });
        });
      }

      // Track winning bid
      analytics.track({
        name: ANALYTICS_EVENTS.QUOTE_WON,
        category: 'transaction',
        properties: {
          bid_id: winningBidId,
          job_id: jobId,
          amount_cents: totalAwardAmount,
        },
      });

      // Save award feedback if provided (non-blocking with retry - errors logged but don't fail the award)
      if (feedbackData && feedbackData.selectionReasons.length > 0) {
        const saveFeedbackWithRetry = async (maxRetries = 2, delayMs = 500) => {
          let lastError: Error | null = null;
          
          for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
              // Use auth.uid() directly - RLS policy checks submitted_by_user_id = auth.uid()
              const authUserId = (await supabase.auth.getUser()).data.user?.id;
              const rfqId = winningBid?.rfq_id;
              
              const { error: feedbackError } = await supabase
                .from('rfq_award_feedback')
                .insert({
                  award_id: awardData?.id,
                  rfq_id: rfqId || null,
                  job_id: jobId,
                  winning_bid_id: winningBidId,
                  submitted_by_user_id: authUserId,
                  selection_reasons: feedbackData.selectionReasons,
                  additional_comments: feedbackData.additionalComments || null,
                });

              if (feedbackError) {
                throw feedbackError;
              }
              
              console.log('[useJobAward] Award feedback saved successfully');
              
              // Track feedback submission
              analytics.track({
                name: ANALYTICS_EVENTS.RFQ_AWARD_FEEDBACK_SUBMITTED,
                category: 'engagement',
                properties: {
                  job_id: jobId,
                  award_id: awardData?.id,
                  reasons_count: feedbackData.selectionReasons.length,
                  has_comments: !!feedbackData.additionalComments,
                  reasons: feedbackData.selectionReasons,
                },
              });
              
              return; // Success - exit retry loop
            } catch (err) {
              lastError = err as Error;
              console.warn(`[useJobAward] Feedback save attempt ${attempt + 1} failed:`, err);
              
              if (attempt < maxRetries) {
                // Exponential backoff
                await new Promise(resolve => setTimeout(resolve, delayMs * (attempt + 1)));
              }
            }
          }
          
          console.error('[useJobAward] Error saving award feedback after retries:', lastError);
        };

        // Fire and forget with retry - don't await
        saveFeedbackWithRetry().catch(() => {
          // Already logged in the function
        });
      }

      // Notify via edge function
      await invokeEdgeFunction('job-award-notify', {
        body: { jobId, winningBidId },
        callerOperation: 'useJobAward.notify',
      });

      // Attempt SPA navigation (React Router)
      try {
        navigate(targetPath);
      } catch (e) {
        // ignore if not within a Router context
      }

      // Fallback: hard redirect to ensure navigation in all environments (e.g., Next routing)
      if (typeof window !== 'undefined') {
        setTimeout(() => {
          if (window.location.pathname !== targetPath) {
            window.location.assign(targetPath);
          }
        }, 300);
      }

      return { award: awardData };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to award job';
      toast({
        title: "Error",
        description: message,
        variant: "destructive"
      });
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return { awardJob, loading };
};
